import colorMaps from './colorMaps'
// @ts-ignore
import { ColorMode, ScalarMode } from '@kitware/vtk.js/Rendering/Core/Mapper/Constants'
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction'

import { vtkContext } from './useVTK.types'

import chroma from 'chroma-js'
import macro, { obj } from '@kitware/vtk.js/macros'
import vtkMatrixBuilder from '@kitware/vtk.js/Common/Core/MatrixBuilder'
import * as vtkMath from '@kitware/vtk.js/Common/Core/Math'

import { VIEW, LEGEND_DEFAULTS, COLORSET_REGEXP, DEFAULT_COLORSET } from './enums'
import { addLegend, getDiscreteColors, updateSectionPlane, updateSectionPlanes } from './useVTK'
import { vec3 } from 'gl-matrix'

import { brandingActor } from './orientationIndicator'

import lbtColorMaps from './lbtColorMaps.json'
import { legendAutoLayout } from './legendLayout'

interface action {
  type: string
  ids?: string[]
  value?: any
}

const prepareColorMap = (colors: any[]) => {
  return ({
    ColorSpace: 'RGB',
    Name: 'Custom',
    NanColor: [0,0,1],
    RGBPoints: colors
      .reduce((p, c, i, arr) => {
        if (!p) {
          p = []
        }
        
        p.push(i / (arr.length - 1))
        p.push(c.r / 255)
        p.push(c.g / 255)
        p.push(c.b / 255)
        
        return p
      }, [])
  })
}

const getArrayByLocation = (mapperSource: any, arrayName: string, i: any) => {

  const pointData = mapperSource.getPointData()
  const cellData = mapperSource.getCellData()

  const point = pointData.getArrayByIndex(i)
  const cell  = cellData.getArrayByIndex(i)

  return { cell, point }
}

// Utility functions - we need more of these
const getLegendSettings = (item, colorByArrayName, i) => {
  let units = undefined
  let colors = lbtColorMaps[DEFAULT_COLORSET]

  const mapperSource = item.mapper.getInputData()

  const { cell, point } = getArrayByLocation(mapperSource, colorByArrayName, i)

  const activeArray = cell ? cell : point
  
  let range = activeArray.getRange().slice(0, 3)

  if (
    item.defaultSettings && 
    item.defaultSettings.metadata && 
    item.defaultSettings.metadata.length > 0
  ) {
    const defaultSettings = item.defaultSettings
      ?.metadata.find(l => (colorByArrayName === l?.data_type?.name) 
      || (colorByArrayName === l?.legend_parameters?.title))

    if (defaultSettings) {
      const defaultLegend = defaultSettings?.legend_parameters
      const min = typeof defaultLegend.min === 'number' ? defaultLegend.min : undefined
      const max = typeof defaultLegend.max === 'number' ? defaultLegend.max : undefined
      
      if (typeof min === 'number' && typeof max === 'number') {
        range = [min, max]
      }

      if(defaultLegend) {
        units = defaultSettings.unit
        colors = defaultLegend.colors && defaultLegend.colors.length > 0 ? prepareColorMap(defaultLegend.colors) : lbtColorMaps[0]
      }
    }
  }

  // Old legend schema
  if (item.defaultSettings && 
    item.defaultSettings.legends && 
    item.defaultSettings.legends.length > 0
  ) {
    const oldLegendObj = item.defaultSettings.legends.find(val => val.name === colorByArrayName)

    range = oldLegendObj.range
    units = oldLegendObj.unit
  }
  
  return {range, units, colors}
}

/*
 * vtkReducer is a state reducer that operates on vtk objects.
 * vtk objects are passed in as state, which is actually a ref object
 * the vtkReducer transforms the vtk scene through calls to vtkjs
 * the new state that is consumed by React is then derived in useVTK
 * the purpose of this system is to treat the vtk scene as our state store
 * it avoids needing to keep a parallel state for consumption by react.
 */
export const vtkReducer = (state: vtkContext, action: action) => {
  const { data, renderer, interactor, orientationWidget, brandingWidget, widgetManager, renderWindow } = state
  if (!data || !renderer || !orientationWidget || !brandingWidget || !widgetManager || !interactor || !renderWindow) return state

  const { type, ids, value } = action

  // if ids is undefined operate on all objects in state
  // else include ids in actions.ids
  const items: any[] = data.filter((item: any) => !ids || ids.includes(item.id))

  // mimics reducer for scene effects
  switch (type) {
    case 'reset-camera': {
      renderer.resetCamera()

      const camera = renderer.getActiveCamera()
      const focalPoint = camera.getFocalPoint()
      
      orientationWidget.updateMarkerOrientation()
      interactor.getInteractorStyle().setCenterOfRotation(focalPoint)
      return
    }
    case 'background-1': {
      renderer.setBackground(value)
      return
    }
    case 'toggle-ortho': {
      const camera = renderer.getActiveCamera()
      camera.setParallelProjection(value)
      // TODO: this doesn't successfully update the unicam focus dot
      // const unicamManipulator = interactor.getInteractorStyle()
      //   .getMouseManipulators()
      //   .filter(m => m.isA('vtkMouseCameraUnicamRotateManipulator'))[0]
      // unicamManipulator.setDisplayFocusSphereOnButtonDown(value)
      return
    }
    case 'select-view': {
      if(typeof VIEW[value] === 'undefined') return
      const camera = renderer.getActiveCamera()
      const { up, direction } = VIEW[value]
      const focalPoint = camera.getFocalPoint()
      const position = camera.getPosition()
      const distance = Math.sqrt(
        vtkMath.distance2BetweenPoints(position, focalPoint)
      )
      camera.setPosition(
        focalPoint[0] + direction[0] * distance,
        focalPoint[1] + direction[1] * distance,
        focalPoint[2] + direction[2] * distance
      )
      camera.setViewUp(up)
      // reset-camera
      interactor.getInteractorStyle().setCenterOfRotation(focalPoint)
      orientationWidget.updateMarkerOrientation()
      renderer.resetCamera()
      return
    }
    case 'rotate-camera': {
      const camera = renderer.getActiveCamera()
      const position = camera.getPosition()
      const transform = vtkMatrixBuilder.buildFromDegree().rotateZ(value).apply(position).getMatrix()
      const focalPoint = camera.getFocalPoint()
      camera.applyTransform(transform)
      // reset-camera
      interactor.getInteractorStyle().setCenterOfRotation(focalPoint)
      orientationWidget.updateMarkerOrientation()
      renderer.resetCamera()
      return
    }
    case 'toggle-gimbal': {
      orientationWidget.getRenderer().setDraw(value)
      return
    }
    case 'toggle-branding': {
      brandingWidget.getRenderer().setDraw(value)
      return
    }
    case 'toggle-branding-size': {
      // TODO: This doesn't look great yet
      const actor = brandingActor(renderWindow.render, value)
      brandingWidget.setActor(actor)
      brandingWidget.setViewportSize(value ? 0.25 : 0.1)
      brandingWidget.setPadding(value ? { x: .1, y: .25 } : { x: 0.475, y: -0.15 })
      return
    }
    case 'adjust-gimbal': {
      orientationWidget.setViewportSize(value)
      return
    }
    case 'toggle-section-box': {
      widgetManager.getWidgets().forEach((widget: any) => {
        if(!widget.isA('vtkPlaneWidget')) return
        widget.setEnabled(value)
        widget.setVisibility(value)
      })
      return
    }
    case 'reset-section-box': {
      const bounds = renderer.computeVisiblePropBounds()
      if(value) {
        widgetManager.getWidgets().forEach((widget: any) => {
          if(!widget.isA('vtkPlaneWidget')) return
          const normal = widget.getWidgetState().getNormal()
          if (vec3.equals(normal, value)) {
            updateSectionPlane(widget, bounds, renderer)
          }
        })
      } else {
        updateSectionPlanes(bounds, renderer, widgetManager)
      }
      renderWindow.render()
      renderer.resetCameraClippingRange()
      return
    }
    case 'set-bounds-section-box': {
      updateSectionPlanes(value, renderer, widgetManager)
      renderWindow.render()
      renderer.resetCameraClippingRange()
      return
    }
    case 'hide-section-plane': {
      const { toggle, normal } = value
      widgetManager.getWidgets().forEach((widget: any) => {
        if (vec3.equals(widget.getWidgetState().getNormal(), normal)) {
          widget.setEnabled(toggle)
          widget.setVisibility(toggle)
        }
      })
      return
    }
    case 'remove-all': {
      renderer.removeAllActors()
      data.forEach((d) => {
        d.actor.delete()
        d.source.delete()
        d.mapper.delete()
        d.id = ''
        d.name = ''
      })
      data.length = 0

      return
    }
    case 'add': {
      items.forEach((item) => {
        if (macro.isVtkObject(item) && item.isA('vtkActor')) renderer.addActor(item)
      })
      return
    }
    case 'visibility': {
      items.forEach((item: any, i: number) => {
        item.actor.setVisibility(value)

        const legend = item.actor.get('legend').legend
        if (!legend) return
        legend.setVisibility(value)
      })
      return
    }
    case 'color': {
      items.forEach((item: any, i: number) => {
        const prop = item.actor.getProperty()
        const rgb = chroma(value).gl()
        prop.setDiffuseColorFrom(rgb.slice(0, 3))
      })
      return
    }
    case 'opacity': {
      items.forEach((item: any, i: number) => {
        const prop = item.actor.getProperty()
        prop.setOpacity(value)
      })
      return
    }
    case 'representation': {
      items.forEach((item: any, i: number) => {
        const prop = item.actor.getProperty()
        const valNum = value === 'POINTS' ? 0 : value === 'WIREFRAME' ? 1 : 2
        prop.setRepresentation(valNum)
      })
      return
    }
    case 'pointSize': {
      items.forEach((item: any, i: number) => {
        const prop = item.actor.getProperty()
        prop.setPointSize(Math.round(value))
      })
      return
    }
    case 'isolate-data': {
      data.forEach((d) => {
        if (value) {
          d.actor.setVisibility(true)
        } else {
          const pointArrays = d.mapper.getInputData().getPointData().getNumberOfArrays()
          const cellArrays = d.mapper.getInputData().getCellData().getNumberOfArrays()
          if ((pointArrays + cellArrays) > 0) {
            d.actor.setVisibility(true)
          } else {
            d.actor.setVisibility(false)
          }
        }
      })
      return
    }
    case 'edgeVisibility': {
      items.forEach((item: any, i: number) => {
        const prop = item.actor.getProperty()
        prop.setEdgeVisibility(value)
      })
      return
    }
    case 'edgeColor': {
      items.forEach((item: any, i: number) => {
        const prop = item.actor.getProperty()
        const rgb = chroma(value).gl()
        prop.setEdgeColorFrom(rgb.slice(0, 3))
      })
      return
    }
    case 'color-by': {
      const colorByArrayName = value
      items.forEach((item: any, i: number) => {
        if (!item.mapper) throw new Error('useVTK: no mapper for scalar data display')

        item.mapper.setUseLookupTableScalarRange(true)

        const cellData = item.mapper.getInputData().getCellData().getArrayByIndex(i)
        const pointData = item.mapper.getInputData().getPointData().getArrayByIndex(i)

        if (!cellData && !pointData) return

        let location = ''
        if (pointData) location = 'PointData'
        if (cellData) location = 'CellData'

        const array = cellData ?? pointData ?? undefined

        const interpolateScalarsBeforeMapping = location === 'PointData'

        // color by solid color
        if (location === '') {
          item.mapper?.set({
            colorByArrayName,
            colorMode: ColorMode.DEFAULT,
            interpolateScalarsBeforeMapping,
            scalarMode: ScalarMode.DEFAULT,
            scalarVisibility: false,
          })

          const color = [0.5, 0, 0]
          const prop = item.actor.getProperty()
          prop.setDiffuseColorFrom(color)

          return
        }

        const colorMode = ColorMode.MAP_SCALARS

        const scalarMode = location === 'PointData' ?
          ScalarMode.USE_POINT_FIELD_DATA :
          ScalarMode.USE_CELL_FIELD_DATA

        item.mapper?.set({
          colorByArrayName,
          colorMode,
          interpolateScalarsBeforeMapping,
          scalarMode,
          scalarVisibility: location !== '',
        })

        const info = item.mapper.get('dataRangeOptions').dataRangeOptions

        const { units, range, colors: preset } = getLegendSettings(item, colorByArrayName, i)
        let uRange = range || array.getRange()

        // Old method
        const defaultColorMapIndex = colorMaps.rgbPresetNames
        .findIndex(name => name.toLowerCase().replace(COLORSET_REGEXP, '')
        .includes(info[colorByArrayName]?.colorset.toLowerCase().replace(COLORSET_REGEXP, '')))
        const defaultColorMap = defaultColorMapIndex !== -1 ? colorMaps.rgbPresetNames[defaultColorMapIndex] : DEFAULT_COLORSET
    
        let oldPreset = undefined
        if (defaultColorMap !== DEFAULT_COLORSET) {
          oldPreset = colorMaps.getPresetReverse(defaultColorMap) 
        } else {
          oldPreset = colorMaps.getPresetReverse(DEFAULT_COLORSET)
        }

        if (!info[colorByArrayName]?.ordinalDictionary) {
          const lut = vtkColorTransferFunction.newInstance()
          lut.applyColorMap(preset || oldPreset)
          lut.setMappingRange(uRange[0], uRange[1])
          lut.updateRange()
      
          item.mapper.setLookupTable(lut)
        } else {
          const dictLen = Object.values(info[colorByArrayName]?.ordinalDictionary).length
          const lut = getDiscreteColors(preset || oldPreset, uRange, dictLen)
          item.mapper.setLookupTable(lut)
        }

        item.actor.get( 'legend' ).legend.delete()

        const label = units ? `${colorByArrayName} (${units})` : colorByArrayName

        const dictInfo = info[colorByArrayName]?.ordinalDictionary 
          ? info[colorByArrayName]?.ordinalDictionary
          : undefined

        const legend = addLegend(item.mapper, label, units, renderer, 
          info[colorByArrayName]?.legendSlot ?? 'RIGHT', dictInfo)

        item.mapper.set({ 'colorSet': 'Custom' })

        legend.setMapper(item.mapper)

        const axisLabel = units ? `${colorByArrayName} (${units})` : colorByArrayName
        
        if (colorByArrayName === 'Data') legend.setAxisLabel('')
        else legend.setAxisLabel(axisLabel)
        
        item.actor.set({ legend })
      })
      return
    }
    case 'color-set': {
      items.forEach((item: any, i: number) => {
        
        if (!item.mapper) throw new Error('useVTK: no mapper for scalar data display')

        const colorByArrayName = item.mapper.getColorByArrayName()

        const cellData = item.mapper.getInputData().getCellData().getArrayByIndex(i)
        const pointData = item.mapper.getInputData().getPointData().getArrayByIndex(i)

        if (!cellData && !pointData) return

        const search = value.toLowerCase().replace(COLORSET_REGEXP, '')

        const presetName = colorMaps.rgbPresetNames
          .find(name => {
            return name.toLowerCase().replace(COLORSET_REGEXP, '').startsWith(search) || 
              name.toLowerCase().replace(COLORSET_REGEXP, '').endsWith(search)
          })

        if (!presetName) return

        let location = ''
        if (pointData) location = 'PointData'
        if (cellData) location = 'CellData'
                
        const interpolateScalarsBeforeMapping = location === 'PointData'

        const colorMode = ColorMode.MAP_SCALARS
        const scalarMode = location === 'PointData' ?
          ScalarMode.USE_POINT_FIELD_DATA :
          ScalarMode.USE_CELL_FIELD_DATA

        // preserve existing range
        const range = item.mapper.getLookupTable().getMappingRange()

        item.mapper?.set({
          value: presetName,
          colorMode,
          interpolateScalarsBeforeMapping,
          scalarMode,
          scalarVisibility: true,
        })
        
        item.mapper.set({ 'colorSet': presetName })

        const preset = colorMaps.getPresetByName(presetName)

        const info = item.mapper.get('dataRangeOptions').dataRangeOptions

        if (info[colorByArrayName]?.ordinalDictionary) {
          const dictLen = Object.values(info[colorByArrayName]?.ordinalDictionary).length
          const lut = getDiscreteColors(preset, range, dictLen)
          item.mapper.setLookupTable(lut)
          item.segmentCount = item.segmentCount
        } else {
          const lut = vtkColorTransferFunction.newInstance()
          lut.applyColorMap(preset)
          lut.setMappingRange(range[0], range[1])
          lut.updateRange()
      
          item.mapper.setLookupTable(lut)
          item.segmentCount = 0
        }

        const legend = item.actor.get( 'legend' ).legend
        if (!legend) return

        legend.setScalarsToColors(item.mapper.getLookupTable())
      })
      return
    }
    case 'data-range': {
      const { dataRange } = value
      items.forEach((item: any, i: number) => {

        const inputData = item.mapper.getInputData()
        const cellDataArray = inputData.getCellData().getNumberOfArrays()
        const pointDataArray = inputData.getPointData().getNumberOfArrays()

        const scalarVisibility = (cellDataArray + pointDataArray > 0)
        if (!scalarVisibility) return

        if (!item.mapper) throw new Error('useVTK: no mapper for scalar data display')

        // if ordinal dict
        if (item?.segmentCount > 0) return

        item.mapper.setUseLookupTableScalarRange(true)

        const lookupTable = item.mapper.getLookupTable()
        lookupTable.setMappingRange(dataRange[0], dataRange[1])
        lookupTable.updateRange()
      })
      return
    }
    case 'reset-data-range': {
      items.forEach((item: any, i: number) => {
        const colorBy = item.mapper.getColorByArrayName()
        if(!colorBy) return
        
        const inputData = item.mapper.getInputData()
        const cellDataArray = inputData.getCellData().getArrayByIndex(i)
        const pointDataArray = inputData.getPointData().getArrayByIndex(i)

        const scalarVisibility = (!cellDataArray || !pointDataArray)
        if (!scalarVisibility) return

        item.mapper.setUseLookupTableScalarRange(true)

        const lookupTable = item.mapper.getLookupTable()
        
        // if item.defaultSettings.legends.find().range use that
        // otherwise set range to data range
        let range = cellDataArray ? cellDataArray.getRange() : pointDataArray.getRange()
        
        // Old version
        let legend
        if (item.defaultSettings?.legends) {
          legend = item.defaultSettings.legends.find(val => val.name === colorBy)
          if (legend) range = legend.range
        }

        // New version
        if (item.defaultSettings?.metadata?.find(l => l?.legend_parameters !== undefined)) {
          legend = item.defaultSettings.metadata.find(l => colorBy === l.data_type.name 
            || colorBy === l?.legend_parameters?.title) 
          if (!legend) return

          const min = typeof legend.legend_parameters.min === 'number' ? legend.legend_parameters.min : undefined
          const max = typeof legend.legend_parameters.max === 'number' ? legend.legend_parameters.max : undefined
          
          if (typeof min === 'number' && typeof max === 'number') {
            range = [min, max]
          }
        }

        lookupTable.setMappingRange(range[0], range[1])
        lookupTable.updateRange()
      })
      return
    }
    case 'reset-data-full': {
      items.forEach((item: any, i: number) => {
        const colorBy = item.mapper.getColorByArrayName()
        if(!colorBy) return
        
        const inputData = item.mapper.getInputData()
        const cellDataArray = inputData.getCellData().getArrayByIndex(i)
        const pointDataArray = inputData.getPointData().getArrayByIndex(i)

        const scalarVisibility = (!cellDataArray || !pointDataArray)
        if (!scalarVisibility) return

        // if ordinal dict
        if (item?.segmentCount > 0) return

        item.mapper.setUseLookupTableScalarRange(true)

        const lookupTable = item.mapper.getLookupTable()
        
        const range = cellDataArray ? cellDataArray.getRange() : pointDataArray.getRange()
        
        lookupTable.setMappingRange(range[0], range[1])
        lookupTable.updateRange()
      })
      return
    }
    case 'legend-label': {
      items.forEach((item: any, i: number) => {
        const legend = item.actor.get( 'legend' ).legend
        if (!legend) return
        
        const { units } = legend.get('units')

        const label = units ? `${value} (${units})` : value
        
        legend.setAxisLabel(label)

        const mapper = legend.getMapper()

        if (mapper) {
          mapper.getLookupTable().resetAnnotations()
        }
      })

      return
    }
    case 'legend-slot': {
      const slots = ['LEFT', 'BOTTOM', 'RIGHT', 'TOP']

      console.log(value)

      if (!slots.includes(value)) {
        return
      }

      items.forEach((item: any, i: number) => {
        const legend = item.actor.get( 'legend' ).legend
        if (!legend) return

        console.log(legend)
        
        legend.setAutoLayout(legendAutoLayout(value))
        legend.set({ slot: value })

        const mapper = legend.getMapper()

        if (mapper) {
          mapper.getLookupTable().resetAnnotations()
        }
      })

      return
    }
    case 'toggle-legend-nan': {
      items.forEach((item: any, i: number) => {
        const legend = item.actor.get( 'legend' ).legend

        if (!legend) return
        
        legend.setDrawNanAnnotation(value)

        const mapper = legend.getMapper()

        if (mapper) {
          mapper.getLookupTable().resetAnnotations()
        }
      })

      return
    }
    case 'toggle-legend-visibility': {

      items.forEach((item: any, i: number) => {
        const legend = item.actor.get( 'legend' ).legend
        
        if (!legend) return
        
        legend.setVisibility(value)
      })
      
      return
    }
    default: {
      return
    }
  }
}